import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { actions, NAVIGATION_NAMESPACE } from '../../store/modules/navigation';
import type { OmitFirst } from '../../types';
import { RouteName } from '../../routes/routes.constants';
import { FeatureEnabledV2, MagicConfigMixin } from '@gig/magic-common';
import { Feature } from '../../util';
import {
    GAMES_SCROLL_DOWN,
    MESSAGING_LOADED,
    MESSAGING_UNREAD_LIST,
    REWARD_COUNT,
    USER_LOGGED_IN_UPDATED,
} from '@gig/magic-messages';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import { NotLocalized } from '@gig/magic-sdk';
import { IMessage } from '@gig/magic-sdk/dist/adapter/messages/message';

const navigationModule = namespace(NAVIGATION_NAMESPACE);

/**
 * The bottom navigation component displays the lower
 * mobile navigation items.
 */
@Component
export default class BottomNavigation extends Mixins(MagicConfigMixin) {
    RouteName = RouteName;

    @FeatureEnabledV2({ slug: Feature.DYNAMIC_OPTION_MOBILE_BOTTOM_MENU })
    isDynamicMenuOptionEnabled: boolean = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;

    /**
     * Number of unread messages
     */
    messages = 0;

    /**
     * Number of pending rewards
     */
    rewards: number = 0;

    /**
     * Emits the action to toggle the menu.
     */
    @navigationModule.Action('toggleMainMenu')
    toggleMenuAction!: OmitFirst<typeof actions.toggleMainMenu>;

    isLoggedIn = false;

    private messageSubscription: ISubscription | undefined;
    private rewardCountSubscription: ISubscription | undefined;
    private loggedInSubscription: ISubscription | undefined;

    async created() {
        this.messageSubscription = window.eventBus.subscribe<boolean>(
            MESSAGING_LOADED,
            async (event) => {
                if (!event.payload) {
                    this.messages = 0;
                    return;
                }

                this.messages =
                    (
                        await window.queryBus.query<
                            Array<IMessage<NotLocalized>>
                        >(MESSAGING_UNREAD_LIST)
                    )?.length ?? 0;
            },
            { replay: true },
        );

        this.rewardCountSubscription = window.eventBus.subscribe<number>(
            REWARD_COUNT,
            (e) => {
                this.rewards = e.payload ?? 0;
            },

            { replay: true },
        );

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            (e) => {
                this.isLoggedIn = e.payload ?? false;
            },

            { replay: true },
        );
    }

    destroyed() {
        this.messageSubscription?.unsubscribe();
        this.rewardCountSubscription?.unsubscribe();
        this.loggedInSubscription?.unsubscribe();
    }

    /**
     * Toggles open/close the menu.
     */
    toggleMenu(event: Event) {
        event.preventDefault();
        this.toggleMenuAction();
    }

    gamesLinkClicked() {
        const alreadyOnHome = this.$route.name === RouteName.HOME;
        const alreadyOnGames = this.$route.name === RouteName.GAMES;

        if (alreadyOnHome || alreadyOnGames) {
            window.commandBus.publish(GAMES_SCROLL_DOWN);
            return;
        }
    }

    get isBingoLobby() {
        return this.$route.name === RouteName.BINGO_LOBBY;
    }

    get shouldDisplayRewards() {
        return (
            this.isDynamicMenuOptionEnabled && this.isLoggedIn && this.rewards
        );
    }

    get shouldDisplayMessages() {
        return (
            !this.isDynamicMenuOptionEnabled ||
            (this.isLoggedIn && this.messages)
        );
    }

    get shouldDisplayBottomBar() {
        if (this.isBingoLobby || this.isTopNavigationEnabled) {
            return false;
        }

        if (this.$route.name !== RouteName.SPORTSBOOK) {
            return true;
        }

        return false;
    }
}
